import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import { useConfirm } from "../context/ConfirmContext";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";

export default function RolesPage() {
  const { canViewRoles, canCreateRoles, canEditRoles, canDeleteRoles } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { confirm } = useConfirm();

  const [roles, setRoles] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Editor state
  const [editingRole, setEditingRole] = useState(null); // null = list view, object = editing
  const [form, setForm] = useState({ name: "", description: "", permissions: [], nap_locator_companies: [] });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Duplicate modal state
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState("");

  // Delete modal state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!canViewRoles) navigate("/dashboard", { replace: true });
  }, [canViewRoles, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [rolesRes, registryRes, companiesRes] = await Promise.all([
        api.get("/roles"),
        api.get("/roles/registry"),
        api.get("/roles/companies"),
      ]);
      setRoles(rolesRes.data);
      setRegistry(registryRes.data);
      setCompanies(companiesRes.data);
    } catch {
      setError("Failed to load roles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleNewRole = () => {
    setEditingRole("new");
    setForm({ name: "", description: "", permissions: [], nap_locator_companies: [] });
    setFormError("");
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setForm({
      name: role.name,
      description: role.description || "",
      permissions: [...role.permissions],
      nap_locator_companies: [...(role.nap_locator_companies || [])],
    });
    setFormError("");
  };

  const handleCancel = () => {
    setEditingRole(null);
    setFormError("");
  };

  const togglePermission = (key) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((k) => k !== key)
        : [...prev.permissions, key],
    }));
  };

  const toggleCategory = (categoryPerms) => {
    const keys = categoryPerms.map((p) => p.key);
    const allSelected = keys.every((k) => form.permissions.includes(k));
    if (allSelected) {
      setForm((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((k) => !keys.includes(k)),
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...keys])],
      }));
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormError("Role name is required.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      if (editingRole === "new") {
        await api.post("/roles", form);
      } else {
        await api.put(`/roles/${editingRole.id}`, form);
      }
      setEditingRole(null);
      await fetchData();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Failed to save role.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (!(await confirm({
      title: "Delete Role",
      message: `Delete role "${role.name}"? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    }))) return;
    try {
      await api.delete(`/roles/${role.id}`);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete role.");
    }
  };

  // Duplicate modal handlers
  const openDuplicateModal = (role) => {
    setDuplicateSource(role);
    setDuplicateName(`${role.name} (Copy)`);
    setDuplicateError("");
  };

  const handleDuplicateConfirm = async () => {
    if (!duplicateName.trim()) {
      setDuplicateError("Role name is required.");
      return;
    }
    setDuplicating(true);
    setDuplicateError("");
    try {
      await api.post(`/roles/${duplicateSource.id}/duplicate`, { name: duplicateName.trim() });
      setDuplicateSource(null);
      await fetchData();
    } catch (err) {
      setDuplicateError(err.response?.data?.detail || "Failed to duplicate role.");
    } finally {
      setDuplicating(false);
    }
  };

  // Delete modal handlers
  const openDeleteModal = (role) => {
    setDeleteTarget(role);
    setDeleteError("");
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await api.delete(`/roles/${deleteTarget.id}`);
      setDeleteTarget(null);
      await fetchData();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Failed to delete role.");
    } finally {
      setDeleting(false);
    }
  };

  // ---------- Role Editor ----------
  if (editingRole) {
    const isNew = editingRole === "new";
    const totalPerms = registry.reduce((sum, cat) => sum + cat.permissions.length, 0);
    const selectedCount = form.permissions.length;

    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        {isMobile ? <MobileNavbar /> : <Navbar />}
        <main className="flex-1 p-4 lg:p-6 max-w-[1200px] w-full mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-xl font-bold text-gray-800">
              {isNew ? "Create New Role" : `Edit Role: ${editingRole.name}`}
            </h1>
            <button
              onClick={handleCancel}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              ← Back to Roles
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3 mb-4">
              {formError}
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="e.g. Dispatcher"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Brief description of this role"
                />
              </div>
            </div>

            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-800">
                Permissions ({selectedCount}/{totalPerms})
              </h2>
              <button
                type="button"
                onClick={() => {
                  const allKeys = registry.flatMap((cat) => cat.permissions.map((p) => p.key));
                  setForm((f) => ({
                    ...f,
                    permissions: f.permissions.length === allKeys.length ? [] : allKeys,
                  }));
                }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                {selectedCount === totalPerms ? "Deselect All" : "Select All"}
              </button>
            </div>

            <div className="space-y-4">
              {registry.map((cat) => {
                const catKeys = cat.permissions.map((p) => p.key);
                const allCatSelected = catKeys.every((k) => form.permissions.includes(k));
                const someCatSelected = catKeys.some((k) => form.permissions.includes(k));

                return (
                  <div key={cat.category} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        checked={allCatSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someCatSelected && !allCatSelected;
                        }}
                        onChange={() => toggleCategory(cat.permissions)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm font-semibold text-gray-700">{cat.category}</span>
                      <span className="text-xs text-gray-400">
                        ({catKeys.filter((k) => form.permissions.includes(k)).length}/{catKeys.length})
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 pl-6">
                      {cat.permissions.map((perm) => (
                        <label
                          key={perm.key}
                          className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={form.permissions.includes(perm.key)}
                            onChange={() => togglePermission(perm.key)}
                            className="w-3.5 h-3.5 text-blue-600 rounded"
                          />
                          <span className="text-gray-700">{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* NAP Locator Company Access */}
          {form.permissions.some((p) => p.startsWith("nap_locator.") || p === "page.nap_locator") && companies.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-800">
                  NAP Locator — Allowed Companies
                  <span className="text-xs text-gray-400 font-normal ml-2">
                    ({form.nap_locator_companies.length === 0 ? "All" : `${form.nap_locator_companies.length} selected`})
                  </span>
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setForm((f) => ({
                      ...f,
                      nap_locator_companies: f.nap_locator_companies.length === companies.length ? [] : companies.map((c) => c.id),
                    }));
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {form.nap_locator_companies.length === companies.length ? "Deselect All" : "Select All"}
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Select which companies this role can access in NAP Locator. If none are selected, all companies are accessible.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {companies.map((company) => (
                  <label
                    key={company.id}
                    className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={form.nap_locator_companies.includes(company.id)}
                      onChange={() => {
                        setForm((f) => ({
                          ...f,
                          nap_locator_companies: f.nap_locator_companies.includes(company.id)
                            ? f.nap_locator_companies.filter((id) => id !== company.id)
                            : [...f.nap_locator_companies, company.id],
                        }));
                      }}
                      className="w-3.5 h-3.5 text-blue-600 rounded"
                    />
                    <span className="text-gray-700">{company.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : isNew ? "Create Role" : "Save Changes"}
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ---------- Roles List ----------
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {isMobile ? <MobileNavbar /> : <Navbar />}
      <main className="flex-1 p-4 lg:p-6 max-w-[1200px] w-full mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-gray-800">Manage Roles & Permissions</h1>
          {canCreateRoles && (
            <button
              onClick={handleNewRole}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded shadow"
            >
              + New Role
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3 mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center text-gray-400 py-16">Loading…</div>
        ) : (
          <div className="grid gap-4">
            {roles.map((role) => (
              <div
                key={role.id}
                className="bg-white rounded-lg shadow p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-base font-semibold text-gray-800">{role.name}</h2>
                    {role.is_system && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">System</span>
                    )}
                  </div>
                  {role.description && (
                    <p className="text-sm text-gray-500 mb-1">{role.description}</p>
                  )}
                  <div className="flex gap-4 text-xs text-gray-400">
                    <span>{role.permissions.length} permissions</span>
                    <span>{role.user_count} user{role.user_count !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {canEditRoles && (
                    <button
                      onClick={() => handleEditRole(role)}
                      className="text-sm bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded"
                    >
                      Edit
                    </button>
                  )}
                  {canCreateRoles && (
                    <button
                      onClick={() => openDuplicateModal(role)}
                      className="text-sm bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded"
                    >
                      Duplicate
                    </button>
                  )}
                  {canDeleteRoles && !role.is_system && (
                    <button
                      onClick={() => openDeleteModal(role)}
                      className="text-sm bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Duplicate Role Modal */}
      {duplicateSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Duplicate Role</h3>
            <p className="text-sm text-gray-600 mb-4">
              Create a copy of <span className="font-medium">"{duplicateSource.name}"</span> with all its permissions.
            </p>
            {duplicateError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-3 py-2 mb-3">{duplicateError}</div>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">New Role Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter new role name"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && !duplicating) handleDuplicateConfirm(); }}
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setDuplicateSource(null)} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
              <button onClick={handleDuplicateConfirm} disabled={duplicating} className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {duplicating ? "Duplicating…" : "Duplicate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Role Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Role</h3>
            <p className="text-sm text-gray-600 mb-1">
              Are you sure you want to delete <span className="font-medium text-red-600">"{deleteTarget.name}"</span>?
            </p>
            <p className="text-sm text-gray-500 mb-4">This action cannot be undone.</p>
            {deleteError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-3 py-2 mb-3">{deleteError}</div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
              <button onClick={handleDeleteConfirm} disabled={deleting} className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
